import React, { useState } from 'react'
import { LinearProgress, Stack, Table, TableBody, TableContainer, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import { IssueTypeText } from '../../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { CompanyEventRow } from './CompanyEventRow'
import { NewIssueQuickButton } from './NewIssueQuickButton'
import { NewIssueQuickForm } from './NewIssueQuickForm'
import { calculateIssueStatus } from '../../../components/utils/calculateIssueStatus'
import IssueApi from '../../../api/IssueApi'
import SourceApi from '../../../api/SourceApi'
import { IssueEntity } from 'types/IssueEntity'
import { PRInputEntity } from 'types/PRInput'
import { SourceType } from 'types/SourceType'
import { CompanyEntity } from 'types/CompanyEntity'
import { SourceEntity } from 'types/SourceEntity'

async function addPRToIssue(issue: IssueEntity, pr: PRInputEntity, sourceType: SourceType) {
    const sourceApi = new SourceApi()

    if (!issue || !pr || !sourceType) {
        console.error(
            `addPRToIssue: Missing ${issue ? '' : 'issue'} ${pr ? '' : 'pr'} ${sourceType ? '' : 'sourceType'}`,
        )
        return null
    }
    /*
    id char(100) not null,
    issue_id char(100) comment 'ID of issue (optional)',
    warrant_id char(100) comment 'Warrant (optional)',
    source_url varchar(1024) comment 'URL of source',
    source_type varchar(50) comment 'Type of source',
    title varchar(250) comment 'Title of source document',
    published_date datetime comment 'Published date and time of source document',
    internal_comment text comment 'Internal comment',
    release_datetime datetime comment 'Release date and time',
    */
    const newSource = {
        issueId: issue.id,
        sourceUrl: pr.url,
        sourceType: sourceType,
        title: pr.title,
        publishedDate: new Date(pr.publishDate),
        releaseDatetime: new Date(pr.publishDate),
    }

    // add source to issue in order for getRightsIssueStage to work
    if (!issue.sources) {
        issue.sources = []
    }
    issue.sources.push(newSource)
    const status = calculateIssueStatus(issue)
    const issueFields = { statusStage: status.stage, statusComplete: status.status === 'complete' }
    const createdSource = await sourceApi.create(newSource, issueFields)
    return createdSource as SourceEntity
}

export const CompanyIssues = ({
    associatedPRs,
    company,
    selectedTargets,
    setAssociatedPRs,
    issues,
    setIssues,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
}: {
    associatedPRs: Record<string, SourceEntity[]>
    company: Pick<CompanyEntity, 'name'>
    issues: IssueEntity[] | null
    selectedTargets: Array<{ id: string }>
    setAssociatedPRs: (prs: Record<string, SourceEntity[]>) => void
    setIssues: (i: IssueEntity[]) => void
    sourceType: SourceType | null
    setSourceType: (s: SourceType | null) => void
    selectedPR: PRInputEntity | null
    onArchivePR: (p: PRInputEntity) => Promise<void>
}) => {
    const [newIssueFormOpen, setNewIssueFormOpen] = useState(false)
    const issueApi = new IssueApi()

    const handleCreateIssue = async (share: { name: string }, issueData) => {
        if (!company) return
        const result = await issueApi.create(issueData)

        if (result) {
            const sourceResult = await addPRToIssue(result, selectedPR, sourceType)
            result.shareName = share.name
            setIssues([result, ...issues])
            if (sourceResult) {
                // no need to add, they will be fetched in the listing
                // associatedPRs[result.id] = [sourceResult]
                // setAssociatedPRs({ ...associatedPRs })
                if (onArchivePR) void onArchivePR(selectedPR)
            }
        }
    }

    if (!issues) return <LinearProgress />

    const enableQuickCreateButton = sourceType === 'Publication'

    return (
        <>
            <NewIssueQuickButton
                company={company}
                onClick={() => setNewIssueFormOpen(true)}
                sx={{ mb: 4 }}
                disabled={!enableQuickCreateButton}
            />
            <NewIssueQuickForm
                company={company}
                open={newIssueFormOpen}
                setOpen={setNewIssueFormOpen}
                handleCreateIssue={handleCreateIssue}
            />
            {issues.length === 0 && <Typography variant='body1'>Inga emissioner hittades</Typography>}
            {issues.length > 0 && (
                <TableContainer>
                    <Table>
                        <TableBody>
                            {issues.map((issue) => (
                                <CompanyEventRow
                                    key={issue.id}
                                    event={issue}
                                    eventType='issue'
                                    selectedRows={selectedTargets}
                                    associatedPRs={associatedPRs}
                                    setAssociatedPRs={setAssociatedPRs}
                                    sourceType={sourceType}
                                    setSourceType={setSourceType}
                                    selectedPR={selectedPR}
                                    onCreateSource={addPRToIssue}
                                    onArchivePR={onArchivePR}
                                >
                                    <Stack>
                                        <Link to={`/admin/issues/${issue.id}`} target='_blank'>
                                            {issue.shareName} -{' '}
                                            {issue.subEndDate
                                                ? formatDateYYYYMMDD(issue.subEndDate)
                                                : formatDateYYYYMMDD(issue.publicationDate)}
                                        </Link>
                                        <i>
                                            <IssueTypeText issueType={issue.type} />
                                        </i>
                                    </Stack>
                                </CompanyEventRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </>
    )
}
